import { useEffect, useState } from 'react';

/**
 * Persistent text-size toggle.
 *
 * Three levels: standard (16px root), large (18px), extra large (20px).
 * Scales the root `font-size`, so every rem-based size in the project
 * grows proportionally — body text, headings, button padding, line height.
 *
 * Persisted in localStorage so the choice survives navigation and reloads.
 * Hydrated synchronously from a tiny snippet in index.html to avoid a
 * visible "flash" of standard size on first paint.
 */

export type TextSize = 'standard' | 'large' | 'x-large';

const STORAGE_KEY = 'wb.textSize';

const ROOT_PX: Record<TextSize, number> = {
  standard: 16,
  large: 18,
  'x-large': 20,
};

const OPTIONS: { id: TextSize; label: string; srLabel: string; fontSize: string }[] = [
  { id: 'standard', label: 'A', srLabel: 'Standard text size', fontSize: '0.85rem' },
  { id: 'large', label: 'A+', srLabel: 'Large text size', fontSize: '0.95rem' },
  { id: 'x-large', label: 'A++', srLabel: 'Extra large text size', fontSize: '1.05rem' },
];

function readStored(): TextSize {
  if (typeof window === 'undefined') return 'standard';
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === 'large' || v === 'x-large' ? v : 'standard';
}

function applyTextSize(size: TextSize) {
  if (typeof document === 'undefined') return;
  document.documentElement.style.fontSize = `${ROOT_PX[size]}px`;
  document.documentElement.setAttribute('data-text-size', size);
}

export function TextSizeToggle() {
  const [size, setSize] = useState<TextSize>('standard');

  // Hydrate from storage on mount and apply.
  useEffect(() => {
    const stored = readStored();
    setSize(stored);
    applyTextSize(stored);
  }, []);

  const choose = (next: TextSize) => {
    setSize(next);
    applyTextSize(next);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* ignore quota / private mode */
    }
  };

  return (
    <div
      role="group"
      aria-label="Text size"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        border: '1px solid var(--rule)',
        borderRadius: 999,
        background: 'white',
        padding: 2,
      }}
    >
      {OPTIONS.map((opt) => {
        const active = size === opt.id;
        return (
          <button
            key={opt.id}
            type="button"
            onClick={() => choose(opt.id)}
            aria-pressed={active}
            aria-label={opt.srLabel}
            title={opt.srLabel}
            style={{
              minWidth: 36,
              minHeight: 36,
              padding: '0 0.45rem',
              border: 'none',
              borderRadius: 999,
              background: active ? 'var(--green)' : 'transparent',
              color: active ? 'var(--cream)' : 'var(--ink-mid)',
              fontFamily: 'var(--font-heading)',
              fontSize: opt.fontSize,
              lineHeight: 1,
              cursor: 'pointer',
              fontWeight: active ? 700 : 600,
            }}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}
